import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NucButtonBarComponent, NucButtonPrimaryComponent, NucFormFieldComponent, NucInputComponent } from '@relayter/rubber-duck';
import { distinctUntilChanged } from 'rxjs';
import { AccountTypeService } from '../../api/services/account-type.service';
import { AuthenticationService } from '../../api/services/authentication.service';
import { AppConstants } from '../../app.constants';
import { Toaster } from '../../classes/toaster.class';
import { AccountTypeModel } from '../../models/response/acount-type.model';

enum ELoginState {
    INIT = 'INIT',
    SSO = 'SSO'
}

@Component({
    selector: 'om-login-component',
    templateUrl: 'login.component.html',
    styleUrls: ['login.component.scss'],
    imports: [ReactiveFormsModule, NucFormFieldComponent, NucInputComponent, NucButtonBarComponent, NucButtonPrimaryComponent]
})
export class LoginComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly accountTypeService = inject(AccountTypeService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly route = inject(ActivatedRoute);

    public readonly ELoginState = ELoginState;
    public formGroup: FormGroup<{ email: FormControl<string> }>;
    public loading = false;
    public nextButtonDisabled = true;
    public state: ELoginState = ELoginState.INIT;
    public email: string;
    private redirectUrl = AppConstants.HOME_PAGE;

    public ngOnInit(): void {
        this.redirectUrl = this.route.snapshot.queryParamMap.get('redirectUrl') ?? AppConstants.HOME_PAGE;
        this.initForms();
        this.showErrorMessage();
    }

    public onSubmitValid(): void {
        if (this.state !== ELoginState.INIT || this.nextButtonDisabled || this.loading) {
            return;
        }

        this.getAccountTypeAndGoToNext();
    }

    private initForms(): void {
        this.formGroup = new FormGroup({
            email: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email] })
        });

        this.formGroup.statusChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe((status) => {
            this.nextButtonDisabled = status !== 'VALID';
        });
    }

    private showErrorMessage(): void {
        const error = this.route.snapshot.queryParamMap.get('error');
        const description = this.route.snapshot.queryParamMap.get('error_description');
        if (error) {
            Toaster.error(description || error, 'Login failed');
        }
    }

    private getAccountTypeAndGoToNext(): void {
        this.email = this.formGroup.getRawValue().email.trim();
        if (!this.email) {
            return;
        }

        this.loading = true;
        this.accountTypeService
            .getAccountTypeByEmail(this.email)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (accountType: AccountTypeModel) => {
                    this.loading = false;
                    switch (accountType.loginFlow) {
                        case 'PASSWORD':
                            this.state = ELoginState.INIT;
                            Toaster.error('Password login is no longer supported. Please contact support.', 'Login');
                            break;
                        case 'SSO':
                            this.state = ELoginState.SSO;
                            this.handleSso(accountType);
                            break;
                        default:
                            this.state = ELoginState.INIT;
                            Toaster.error('Unsupported login flow', 'Login');
                            break;
                    }
                },
                error: (error: unknown) => {
                    this.loading = false;
                    this.state = ELoginState.INIT;
                    Toaster.handleApiError(error as any);
                }
            });
    }

    private handleSso(accountType: AccountTypeModel): void {
        this.loading = true;
        const ssoClientId = accountType.ssoClientId ?? accountType.clientId;

        try {
            this.authenticationService.redirectSso(ssoClientId, this.email, this.redirectUrl);
        } catch (error) {
            this.loading = false;
            this.state = ELoginState.INIT;
            Toaster.error((error as Error).message, 'SSO unavailable');
        }
    }
}
